import { ChevronLeft, ChevronRight } from 'lucide-react'
import type { ReactNode } from 'react'
import {
  addDays,
  fmtLongDate,
  monthTabOf,
  monthTabs,
  WEEKDAYS_SHORT,
  weekDates,
  weekLabel,
  type LocalDate,
} from '../time'

export type CalendarDayStatus = 'available' | 'no_slots' | 'closed' | 'outside_window'

export interface CalendarDay {
  date: LocalDate
  status: CalendarDayStatus
  closedReason: string | null
  slotCount: number
}

export interface WeekCalendarProps {
  weekStart: LocalDate
  /**
   * The week's days (API.md `WeekAvailability.days`); `undefined` while the week loads: the dates shimmer with no
   * status, can't be clicked, and the grid is `aria-busy` (UserFlows P-08 "Loading a week").
   */
  days: CalendarDay[] | undefined
  window: { firstDate: LocalDate; lastDate: LocalDate }
  /** The clinic's today (the "today" mark). */
  today: LocalDate
  value: LocalDate | null
  canPrev: boolean
  canNext: boolean
  onWeekChange: (weekStart: LocalDate) => void
  onPick: (date: LocalDate) => void
  /** Shown over the greyed week when it has no openings (P-08 empty states). */
  emptyWeek: (hasNext: boolean) => ReactNode
}

const dayOfMonth = (d: LocalDate) => Number(d.slice(8, 10))

/**
 * P-08 calendar (UserFlows P-08), ported from the mockup's `components/WeekCalendar.tsx`: this month and next month
 * as tabs, then one Sunday-start week at a time. Each day shows "N slots", "Closed" (tooltip: the reason), "No slots"
 * or nothing outside the bookable range; days without slots are disabled; no day is preselected. Dates are clinic
 * dates (strings), so the browser's time zone never matters.
 */
export function WeekCalendar({
  weekStart,
  days,
  window,
  today,
  value,
  canPrev,
  canNext,
  onWeekChange,
  onPick,
  emptyWeek,
}: WeekCalendarProps) {
  const tabs = monthTabs(window)
  const month = monthTabOf(weekStart, window)
  const byDate = new Map((days ?? []).map((d) => [d.date, d]))
  const dates = weekDates(weekStart)
  const empty = days?.every((d) => d.status !== 'available') ?? false
  /** Loading a week (task 07): the dates with a shimmer instead of a status, not clickable, `aria-busy`. */
  const loading = days === undefined

  return (
    <div className="wk">
      <div className="wk-months" role="tablist" aria-label="Month">
        {tabs.map((tab, i) => (
          <button
            key={tab.label}
            type="button"
            role="tab"
            aria-selected={month === i}
            className="wk-month"
            disabled={tab.disabled}
            onClick={() => {
              onWeekChange(tab.weekStart)
            }}
          >
            {tab.label}
          </button>
        ))}
      </div>
      <div className="wk-head">
        <button
          type="button"
          className="wk-arrow"
          disabled={!canPrev}
          onClick={() => {
            onWeekChange(addDays(weekStart, -7))
          }}
          aria-label="Previous week"
        >
          <ChevronLeft />
        </button>
        <div className="wk-title">{weekLabel(weekStart)}</div>
        <button
          type="button"
          className="wk-arrow"
          disabled={!canNext}
          onClick={() => {
            onWeekChange(addDays(weekStart, 7))
          }}
          aria-label="Next week"
        >
          <ChevronRight />
        </button>
      </div>
      <div className="wk-body">
        <div
          className={`wk-grid ${empty ? 'dim' : ''}`}
          aria-hidden={empty || undefined}
          aria-busy={loading || undefined}
        >
          {dates.map((date) => {
            const day = byDate.get(date)
            const n = day?.status === 'available' ? day.slotCount : 0
            const shut = day?.status === 'closed' ? (day.closedReason ?? 'Closed') : undefined
            const inRange = day !== undefined && day.status !== 'outside_window'
            const label =
              day === undefined
                ? ''
                : day.status === 'available'
                  ? `${String(n)} ${n === 1 ? 'slot' : 'slots'}`
                  : day.status === 'closed'
                    ? 'Closed'
                    : day.status === 'no_slots'
                      ? 'No slots'
                      : ''
            const cls = [
              'wk-day',
              loading && 'loading',
              n > 0 && 'available',
              shut !== undefined && inRange && 'closed',
              date === today && 'today',
              value === date && 'selected',
            ]
              .filter(Boolean)
              .join(' ')
            return (
              <button
                key={date}
                type="button"
                className={cls}
                disabled={n === 0}
                aria-pressed={value === date}
                onClick={() => {
                  onPick(date)
                }}
                title={shut}
              >
                <span className="wk-dow" aria-hidden>
                  {WEEKDAYS_SHORT[dayOfWeekIndex(date, weekStart)]}
                </span>
                <span className="wk-date" aria-hidden>
                  {dayOfMonth(date)}
                </span>
                <span className="wk-count" aria-hidden>
                  {label}
                </span>
                <span className="sr-only">
                  {loading
                    ? fmtLongDate(date)
                    : `${fmtLongDate(date)}${n > 0 ? `, ${String(n)} time slot${n === 1 ? '' : 's'}` : shut !== undefined ? `, closed (${shut})` : ', unavailable'}`}
                </span>
              </button>
            )
          })}
        </div>
        {empty && <div className="wk-overlay">{emptyWeek(canNext)}</div>}
      </div>
    </div>
  )
}

const dayOfWeekIndex = (date: LocalDate, weekStart: LocalDate) =>
  weekDates(weekStart).indexOf(date) as 0 | 1 | 2 | 3 | 4 | 5 | 6
